import { useMemo } from 'react';
import { useAccessControl } from './useAccessControl';
import { menuItems } from '../components/layout/menu/mainMenu';

// Maps menu leaf id → app route path (mirrors App.js pageMap)
const ROUTE_MAP = {
  dashboard: '/',
  students: '/students',
  teachers: '/teachers',
  'fees-types': '/fees/types',
  'fees-groups': '/fees/groups',
  'fees-allocate': '/fees/allocate',
  'fees-payments': '/fees/payments',
  'fees-reports': '/fees/reports',
  'fees-clearance-settings': '/fees/clearance-settings',
  'fees-clearance-cards': '/fees/clearance-cards',
  'accounting-transactions': '/accounting/transactions',
  'accounting-accounts': '/accounting/accounts',
  'accounting-voucher-heads': '/accounting/voucher-heads',
  'academic-levels': '/academic/levels',
  'academic-classes': '/academic/classes',
  'academic-subjects': '/academic/subjects',
  'academic-subject-with-papers': '/academic/subject-with-papers',
  'academic-subject-papers': '/academic/subject-papers',
  'academic-curriculum': '/academic/curriculum',
  'academic-terms': '/academic/terms',
  'academic-subject-allocations': '/academic/subject-allocations',
  'academic-class-allocations': '/academic/class-allocations',
  'assessments-types': '/assessments/types',
  'assessments-configurations': '/assessments/configurations',
  'assessments-marks': '/assessments/marks',
  'assessments-reports': '/assessments/reports',
  'assessments-teacher-workload': '/assessments/teacher-workload',
  'grading-scales': '/grading/scales',
  'grading-boundaries': '/grading/boundaries',
  'grading-class-scales': '/grading/class-scales',
  'report-card-subject-comments': '/report-card/subject-comments',
  'report-card-general-comments': '/report-card/general-comments',
  'report-card-student-conduct': '/report-card/student-conduct',
  'report-card-generation': '/report-card/generation',
  'report-card-hub': '/report-card/hub',
  'report-card-comment-templates': '/report-card/comment-templates',
  'report-card-conduct-categories': '/report-card/conduct-categories',
  'report-card-conduct-ratings': '/report-card/conduct-ratings',
  'report-card-templates': '/report-card/templates',
  communications: '/communications',
  'communications-logs': '/communications/logs',
  'communications-email-management': '/communications/emails',
  'communications-sms': '/communications/sms',
  'settings-account': '/settings/account',
  'settings-users': '/settings/users',
  'settings-roles': '/settings/roles',
  'settings-rbac': '/settings/rbac',
  'settings-school': '/settings/school',
};

// Collect all leaf ids in sidebar order (depth-first)
function collectLeaves(items) {
  const leaves = [];
  for (const item of items) {
    if (!item.children) {
      leaves.push(item);
    } else {
      for (const child of item.children) {
        if (child.type === 'group') {
          for (const leaf of child.children || []) leaves.push(leaf);
        } else {
          leaves.push(child);
        }
      }
    }
  }
  return leaves;
}

const ORDERED_LEAVES = collectLeaves(menuItems);

export const useFirstAccessibleRoute = () => {
  const { canAccess, canAny, canAll } = useAccessControl();

  return useMemo(() => {
    for (const leaf of ORDERED_LEAVES) {
      const route = ROUTE_MAP[leaf.id];
      if (!route) continue;

      let accessible = false;
      if (leaf.access) {
        const passAll = leaf.access.all ? canAll(leaf.access.all) : true;
        const passAny = leaf.access.any ? canAny(leaf.access.any) : true;
        accessible = passAll && passAny;
      } else {
        accessible = canAccess(leaf.permission);
      }

      if (accessible) return route;
    }
    // Absolute fallback — account settings has no permission requirement
    return '/settings/account';
  }, [canAccess, canAny, canAll]);
};
